import { useNavigate } from '@tanstack/react-router'
import type { AdminDeckDto } from '@lunary/api-client'
import { useDecks } from './queries'
import type { DeckKind } from './queries'
import { CreateDeckDialog } from './create-deck-dialog'
import { DeckTable } from './deck-table'
import { deckErrorText } from './errors'
import { KIND_LIVE, KIND_SHORT, KINDS } from './kinds'
import type { DeckStatus, DecksSearch } from './list-search'
import expert from '../../shared/ui/expert.module.css'
import tabs from './decks.module.css'
import styles from './decks-list.module.css'

const STATUSES: readonly { v: DeckStatus | undefined; label: string }[] = [
  { v: undefined, label: 'Все' },
  { v: 'default', label: 'Основные' },
  { v: 'enabled', label: 'Включены' },
  { v: 'disabled', label: 'Выключены' },
]

function matches(d: AdminDeckDto, s: DecksSearch): boolean {
  if (s.kind && d.kind !== s.kind) return false
  if (s.status === 'default' && !d.is_default) return false
  if (s.status === 'enabled' && !d.enabled) return false
  if (s.status === 'disabled' && d.enabled) return false
  const q = s.q?.trim().toLowerCase()
  return !q || d.name.toLowerCase().includes(q) || (d.owner_name ?? '').toLowerCase().includes(q)
}

function rank(d: AdminDeckDto): number {
  return (d.is_default ? 2 : 0) + (d.enabled ? 1 : 0)
}

export function DecksPage({ search, creating }: { search: DecksSearch; creating: boolean }) {
  const decks = useDecks()
  const navigate = useNavigate()
  const items = decks.data?.items ?? []
  const shown = items.filter((d) => matches(d, search)).sort((a, b) => rank(b) - rank(a))
  const count = (patch: DecksSearch) => items.filter((d) => matches(d, { ...search, ...patch })).length
  const setSearch = (patch: DecksSearch, replace = false) => void navigate({ to: '/decks', search: { ...search, ...patch }, replace })
  const open = (deckId: string) => void navigate({ to: '/decks/$deckId', params: { deckId } })
  const filtered = Boolean(search.kind ?? search.status ?? search.q)

  return (
    <div>
      <header className={expert.head}>
        <div>
          <h1>Колоды</h1>
          <p className="muted">Для «Карты дня» (22 старших и свои карты) и для «Трёх карт» (78). Основная колода каждого типа видна пользователям.</p>
        </div>
        <button type="button" onClick={() => void navigate({ to: '/decks/new', search })}>
          Создать колоду
        </button>
      </header>
      {decks.isError && <p className={tabs.error}>{deckErrorText(decks.error)}</p>}
      <div className={styles.live}>
        {KINDS.map((k) => {
          const d = items.find((x) => x.kind === k && x.is_default)
          return (
            <button key={k} type="button" className={styles.liveItem} disabled={!d} onClick={() => d && open(d.id)}>
              {d?.back ? <img src={d.back.thumb_url} alt="" draggable={false} /> : <span className={styles.backStd} aria-hidden="true" />}
              <span>
                <small>{KIND_LIVE[k]}</small>
                {d ? d.name : 'нет основной, стандартные картинки'}
              </span>
            </button>
          )
        })}
      </div>
      <div className={styles.filters}>
        <div className={tabs.tabs} role="group" aria-label="Тип колоды">
          {([undefined, ...KINDS] as const).map((k: DeckKind | undefined) => (
            <button key={k ?? 'all'} type="button" aria-pressed={search.kind === k} onClick={() => setSearch({ kind: k })}>
              {k ? KIND_SHORT[k] : 'Все'} · {count({ kind: k })}
            </button>
          ))}
        </div>
        <div className={tabs.tabs} role="group" aria-label="Статус колоды">
          {STATUSES.map((s) => (
            <button key={s.v ?? 'all'} type="button" aria-pressed={search.status === s.v} onClick={() => setSearch({ status: s.v })}>
              {s.label} · {count({ status: s.v })}
            </button>
          ))}
        </div>
        <input
          className={styles.search}
          type="search"
          aria-label="Поиск по названию или автору"
          placeholder="Название или автор"
          value={search.q ?? ''}
          onChange={(e) => setSearch({ q: e.target.value || undefined }, true)}
        />
      </div>
      {decks.isPending && <div className="spinner" aria-label="Загрузка" />}
      {decks.isSuccess && shown.length > 0 && <DeckTable items={shown} />}
      {decks.isSuccess && shown.length === 0 && (
        <div className={styles.empty}>
          {filtered ? 'Под фильтр ничего не подошло.' : 'Колод пока нет.'}
          {filtered && (
            <button type="button" className="secondary" onClick={() => void navigate({ to: '/decks', search: {} })}>
              Сбросить фильтры
            </button>
          )}
        </div>
      )}
      <CreateDeckDialog
        open={creating}
        onClose={() => void navigate({ to: '/decks', search })}
        onCreated={(deckId) => void navigate({ to: '/decks/$deckId', params: { deckId }, replace: true })}
      />
    </div>
  )
}
